/**
 * Direct-fetch wrapper for the Cloudflare Pages Function at /v1/marketing/waitlist.
 *
 * Intentionally does NOT go through @washed/api-client — the Pages Function has
 * a distinct contract surface from the core-api, and keeping them separate avoids
 * coupling the marketing waitlist to the core-api client config (base-url, auth
 * headers, version prefixing, etc.).
 *
 * Failures are thrown as a WaitlistError carrying a closed `code`, so the forms
 * can tell the visitor what actually went wrong (their number, the anti-bot
 * check, their connection, or our server) instead of one generic message.
 */

import { getMarketingAttribution } from '../lib/marketingAttribution.js';

export interface SubmitInput {
  phone: string;
  email?: string;
  quartier?: string;
  audience: 'subscriber' | 'worker';
  name?: string;
  source: 'hero' | 'closing' | 'worker';
  locale: 'fr' | 'en';
  /** Loi 2019-014 / GDPR consent: must be true for the server to accept the signup. */
  consentTerms: boolean;
  /** Cloudflare Turnstile token (H-2); sent when the widget is enabled. */
  cfTurnstileResponse?: string;
}

export interface SubmitOutput {
  ok: boolean;
  position: number;
  alreadyListed: boolean;
}

export type WaitlistErrorCode =
  | 'invalid_phone'
  | 'invalid_email'
  | 'consent_required'
  | 'challenge_failed'
  | 'network'
  | 'server';

/** The field a code points at, so the form can mark and focus that input. */
export const WAITLIST_ERROR_FIELD: Partial<Record<WaitlistErrorCode, 'phone' | 'email'>> = {
  invalid_phone: 'phone',
  invalid_email: 'email',
};

const SERVER_CODES: ReadonlySet<string> = new Set([
  'invalid_phone',
  'invalid_email',
  'consent_required',
  'challenge_failed',
]);

export class WaitlistError extends Error {
  readonly code: WaitlistErrorCode;
  constructor(code: WaitlistErrorCode, detail?: string) {
    super(detail ? `waitlist ${code}: ${detail}` : `waitlist ${code}`);
    this.name = 'WaitlistError';
    this.code = code;
  }
}

/** Normalises anything a submit can throw into a code the UI has copy for. */
export function waitlistErrorCode(error: unknown): WaitlistErrorCode {
  return error instanceof WaitlistError ? error.code : 'server';
}

export async function submitWaitlist(input: SubmitInput): Promise<SubmitOutput> {
  const attribution = getMarketingAttribution();
  let res: Response;
  try {
    res = await fetch('/v1/marketing/waitlist', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        phone: input.phone,
        email: input.email,
        quartier: input.quartier,
        audience: input.audience,
        locale: input.locale,
        name: input.name,
        source: input.source,
        consentTerms: input.consentTerms,
        cf_turnstile_response: input.cfTurnstileResponse,
        landing_path: attribution?.landingPath,
        referrer: attribution?.referrer,
        utm_source: attribution?.utmSource,
        utm_medium: attribution?.utmMedium,
        utm_campaign: attribution?.utmCampaign,
        utm_content: attribution?.utmContent,
        utm_term: attribution?.utmTerm,
      }),
    });
  } catch (e) {
    // fetch only rejects when the request never got an HTTP answer.
    throw new WaitlistError('network', e instanceof Error ? e.message : String(e));
  }

  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const error = (body as { error?: unknown } | null)?.error;
    const code =
      typeof error === 'string' && SERVER_CODES.has(error)
        ? (error as WaitlistErrorCode)
        : 'server';
    throw new WaitlistError(code, `HTTP ${res.status}`);
  }

  const out = body as Partial<SubmitOutput> | null;
  if (out?.ok !== true || typeof out.position !== 'number') {
    throw new WaitlistError('server', 'unexpected response shape');
  }
  return { ok: true, position: out.position, alreadyListed: out.alreadyListed === true };
}
